import { memo, useCallback, useMemo } from 'react';
import { queryClient } from '@/lib/react-query';
import { useCustomToast } from '@/hooks/use-custom-toast';
import { GET_LIST_INTEGRATION_WHATSAPP_QUERY_KEY } from '../../../api/get-list-integration-whatsapp.api';
import { useSwitchStatusWhatsappIntegration } from '../../../api/switch-status-whatsapp.api';
import { TConnectWhatsapp } from '../../../types/connect-whatsapp.type';
import Banner from '@/components/ui/banner';
import { TriangleAlert } from 'lucide-react';
import { useChannelModalRootActionsStore } from '@/features/channels/_shared/components/channel-modal-root/channel-modal-root.store';
import {
  Modal,
  ModalHeader,
  ModalTitle,
  ModalContent,
  ModalFooter,
  ModalCloseButton,
  ModalConfirmButton,
} from '@/components/_ui/modal';
import { handleDomBubbleEvent } from '@/utils/dom';
import { Spinner } from '@/components/_ui/spinner';

export type TSwitchConnectWhatsappModalType = 'SwitchConnectWhatsappModal';
export interface ISwitchConnectWhatsappModalProps {
  organizationChannelId: string;
  isActive: boolean;
}

const SwitchConnectWhatsappModal = memo(({ organizationChannelId, isActive }: ISwitchConnectWhatsappModalProps) => {
  const showToast = useCustomToast();
  const channelModalRootActions = useChannelModalRootActionsStore();

  const isOpenSwitchConnectWhatsappModal = useMemo(() => {
    return channelModalRootActions.checkIsOpenModal('SwitchConnectWhatsappModal');
  }, [channelModalRootActions]);

  // Get next status changed
  const nextStatusChanged = useMemo(() => !isActive, [isActive]);

  const mutateSwitchStatusWhatsappIntegration = useSwitchStatusWhatsappIntegration({
    mutationConfig: {
      onSuccess: (data) => {
        // console.log('data', data);
        /**
         * If next status is true, then open QR Modal
         * to begin verification process
         */
        if (nextStatusChanged) {
          const responseSuccess = data?.payload as unknown as TConnectWhatsapp;
          channelModalRootActions.openModal('GetQrWhatsappModal', {
            organizationChannelId: responseSuccess?.organizationChannelId || organizationChannelId,
            isPreparingQrCode: false,
          });
          channelModalRootActions.closeModal('SwitchConnectWhatsappModal');
        } else if (!nextStatusChanged) {
          /**
           * If next status is false, then show success toast
           * and invalidate query to get latest data
           */
          queryClient.invalidateQueries({ queryKey: [GET_LIST_INTEGRATION_WHATSAPP_QUERY_KEY] });
          channelModalRootActions.closeModal('SwitchConnectWhatsappModal');
          showToast({ type: 'success', message: 'Whatsapp channel successfully disabled' });
        }
      },
      onError: () => {
        // Remove this switch from loading state on error
        showToast({ type: 'error', message: 'Failed to switch whatsapp status!' });
      },
    },
  });

  const handleCloseModal = useCallback(() => {
    channelModalRootActions.closeModal('SwitchConnectWhatsappModal');
  }, [channelModalRootActions]);

  const handleSwitchConnectWhatsapp = useCallback(() => {
    if (organizationChannelId && !nextStatusChanged) {
      mutateSwitchStatusWhatsappIntegration.mutate({
        organizationChannelId,
        status: nextStatusChanged,
      });
    }
  }, [organizationChannelId, mutateSwitchStatusWhatsappIntegration, nextStatusChanged]);

  return (
    <Modal
      isOpen={isOpenSwitchConnectWhatsappModal}
      lockBodyScroll
      onClose={handleCloseModal}
      isCloseOutside={false}
      className="p-[32px]"
    >
      <ModalHeader>
        <ModalTitle>Disconnect Channel</ModalTitle>
      </ModalHeader>
      <ModalContent>
        <span className="text-[14px] font-normal text-[#9D9D9C]">Do you want to disconnect this channel?</span>
        <Banner
          className="my-[8px]"
          icon={TriangleAlert}
          title="Disconnecting your channel, this may take 1–2 minutes. Please do not close this window!"
          variant="warning"
        />
      </ModalContent>
      <ModalFooter>
        <ModalCloseButton
          onClick={(e) => {
            handleDomBubbleEvent(e);
            handleCloseModal();
          }}
          disabled={mutateSwitchStatusWhatsappIntegration.isPending}
        >
          Cancel
        </ModalCloseButton>
        <ModalConfirmButton
          onClick={(e) => {
            handleDomBubbleEvent(e);
            handleSwitchConnectWhatsapp();
          }}
          disabled={mutateSwitchStatusWhatsappIntegration.isPending}
        >
          {mutateSwitchStatusWhatsappIntegration.isPending ? (
            <Spinner isLoading={true} className="w-[18px] h-[18px]" />
          ) : null}{' '}
          Okay
        </ModalConfirmButton>
      </ModalFooter>
    </Modal>
  );
});

SwitchConnectWhatsappModal.displayName = 'SwitchConnectWhatsappModal';

export default SwitchConnectWhatsappModal;
